import { injectIntl } from "react-intl";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TableHeader from "src/components/TableHeader";
import {
  OFFER_PROBLEMSTABLE_TITLES,
  OFFER_PROBLEMS_TABLE_TITLE_WIDTHS,
  OFFER_PROBLEMS_TABLE_TITLE_SORT,
} from "src/helpers/Constants";
import { OfferTableSortType } from "src/helpers/Enums";
import Pagination from "src/components/Pagination";
import apiService from "src/services/api.service";

const OfferProblems = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location.state || { type: "pending" };
  const limit = 10;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedList, setSortedList] = useState([]);
  const problemType = stateData.type || "pending";
  const [sortBy, setSortBy] = useState("score");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    apiService
      .getOfferSuggestions({
        page: page,
        limit: limit || 10,
        status: problemType,
        sortBy: sortBy,
        sortOrder: sortOrder,
      })
      .then((response) => {
        setSortedList(response.data?.data || []);
        setPage(response.data?.pagination?.page || 1);
        setTotalPages(response.data?.pagination?.totalPages || 1);
        setTotal(response.data?.pagination?.total || 0);
      })
      .catch(() => {
        setSortedList([]);
      });
  }, [page, problemType, limit, sortBy, sortOrder]);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleSortChange = (sortKey, isAscending) => {
    let apiSortBy = "score"; // default

    // Index 0: "Score & Offer Name" -> sortKey = "name" -> send "score"
    if (sortKey === "name") {
      apiSortBy = "score";
    }
    // Index 1: "Problem" -> sortKey = "totalRevenue" -> send "count"
    else if (sortKey === "totalRevenue") {
      apiSortBy = "count";
    }

    setSortBy(apiSortBy);
    setSortOrder(isAscending ? "asc" : "desc");
    setPage(1);
  };

  const CircularProgress = ({ score, size = 40 }) => {
    const radius = size / 2 - 4; // 4px stroke width için padding
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const strokeWidth = 4;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="white"
            stroke="#FFF"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={
              problemType === "pending"
                ? "#DD2E2E"
                : problemType === "noted"
                ? "#F0970B"
                : "#53A852"
            }
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-[#15151F] text-center text-xs font-bold"
          style={{ fontFamily: "Inter" }}
        >
          {score}
        </div>
      </div>
    );
  };

  return (
    <div className={"Page"}>
      <div className={"PageHeader justify-start gap-3 z-10"}>
        <button
          onClick={() => navigate("/offers")}
          className="flex w-[80px] h-[36px] px-3 justify-center items-center gap-1.5 rounded-lg border border-[#E5E7F0] bg-white text-[#15151F] text-xs font-semibold leading-normal"
          style={{
            boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)",
            fontFamily: "Inter",
          }}
        >
          <img
            alt={"Logo"}
            src={"/media/icons/arrowLeft.svg"}
            className={"w-4 h-4"}
          />
          Back
        </button>
        <div className={"PageHeaderTitle"}>
          {problemType === "pending"
            ? `Pending Resolution (${total})`
            : problemType === "noted"
            ? `Noted (${total})`
            : `Resolved (${total})`}
        </div>
      </div>
      <div className={"w-full flex flex-col justify-between h-full"}>
        <div
          className={"w-full flex flex-col px-6 flex-1 min-h-0 overflow-y-auto"}
        >
          <div className="sticky top-0 z-10 bg-white flex-shrink-0 pb-3 -mx-6 px-6">
            <TableHeader
              titles={OFFER_PROBLEMSTABLE_TITLES}
              widths={OFFER_PROBLEMS_TABLE_TITLE_WIDTHS}
              sort={OFFER_PROBLEMS_TABLE_TITLE_SORT}
              sortType={OfferTableSortType}
              setList={props.setList}
              list={props.list}
              onSortChange={handleSortChange}
            />
          </div>
          <div className="TableWrapper border-0 flex-shrink-0">
            <table className="Table">
              <tbody className="flex flex-col gap-3">
                {sortedList && sortedList.length > 0 ? (
                  sortedList.map((item, index) => (
                    <tr
                      key={index}
                      className={`min-h-[72px] rounded-lg flex items-stretch cursor-pointer ${
                        problemType === "pending"
                          ? "bg-[#FFD2D2]"
                          : problemType === "noted"
                          ? "bg-[#FFF4D3]"
                          : "bg-[#E2FFE2]"
                      }`}
                      onClick={() =>
                        navigate("/offers/details", {
                          state: { offerId: item.offer._id },
                        })
                      }
                    >
                      <td
                        className="py-2 px-[10px] text-[12px] leading-[14px] font-semibold self-stretch"
                        style={{
                          width: OFFER_PROBLEMS_TABLE_TITLE_WIDTHS[0],
                        }}
                      >
                        <div className="flex items-center gap-3 h-full py-2">
                          <CircularProgress
                            score={
                              item?.offer?.score
                                ? Math.round(
                                    parseFloat(
                                      String(item.offer.score).replace(",", ".")
                                    ) || 0
                                  )
                                : 0
                            }
                          />
                          <div className="flex items-center justify-between gap-[10px] flex-1">
                            <div>{item?.offer?.name}</div>
                            {item?.offer?.totalRevenue && (
                              <div
                                className={`inline-flex items-center h-[18px] px-[6px] rounded-[34px] flex-shrink-0 ${
                                  problemType === "pending"
                                    ? "bg-[#DD2E2E]"
                                    : problemType === "noted"
                                    ? "bg-[#F0970B]"
                                    : "bg-[#53A852]"
                                } text-white text-[11px] font-semibold`}
                                style={{ fontFamily: "Inter" }}
                              >
                                ${item?.offer?.totalRevenue?.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-[10px] text-[12px] leading-[14px] font-semibold min-w-[1px] flex items-stretch self-stretch">
                        <div
                          className="min-h-full rounded-[4px] w-[1px]"
                          style={{
                            backgroundColor: "rgba(129, 13, 13, 0.40)",
                          }}
                        ></div>
                      </td>
                      <td
                        className="py-2 px-[10px] text-[12px] leading-[14px] font-semibold w-full"
                        style={{ width: OFFER_PROBLEMS_TABLE_TITLE_WIDTHS[1] }}
                      >
                        <div className="flex items-center justify-between gap-3 w-full py-2">
                          <div className="flex items-center gap-2">
                            <img
                              alt={"Logo"}
                              src={
                                problemType === "pending"
                                  ? "/media/icons/pending.svg"
                                  : problemType === "noted"
                                  ? "/media/icons/Solid-warning.svg"
                                  : "/media/icons/Solid-check.svg"
                              }
                              className={"w-4 h-4"}
                            />
                            <div className="flex flex-col gap-[6px]">
                              <div
                                className={`${
                                  problemType === "pending"
                                    ? "text-[#810D0D]"
                                    : problemType === "noted"
                                    ? "text-[#7D510E]"
                                    : "text-[#0D650C]"
                                } text-[13px] font-bold`}
                              >
                                {item?.suggestions?.length || 0}{" "}
                                {problemType === "pending"
                                  ? "Pending Resolution"
                                  : problemType === "noted"
                                  ? "Noted"
                                  : "Resolved"}
                              </div>
                              {item?.suggestions &&
                                item.suggestions.length > 0 && (
                                  <div className="flex flex-col gap-[6px]">
                                    {Array.from(
                                      new Set(
                                        item.suggestions
                                          .map((suggestion) => suggestion.note)
                                          .filter((note) => note)
                                      )
                                    ).map((uniqueNote, index) => (
                                      <div
                                        key={index}
                                        className={`${
                                          problemType === "pending"
                                            ? "text-[#810D0D]"
                                            : problemType === "noted"
                                            ? "text-[#7D510E]"
                                            : "text-[#0D650C]"
                                        } text-xs leading-[18px] font-normal`}
                                        style={{ fontFamily: "Inter" }}
                                      >
                                        {uniqueNote}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {!item?.suggestions && item.description && (
                                <div
                                  className="text-[#810D0D] text-xs leading-[18px] font-normal"
                                  style={{ fontFamily: "Inter" }}
                                >
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                          <button type="button">
                            <img
                              alt={"Logo"}
                              src={"/media/icons/chevron-right-red.svg"}
                              className={"w-4 h-4"}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center py-8 text-gray-500">
                      No problems found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          padding="0 38px 0 24px"
        />
      </div>
    </div>
  );
};

export default injectIntl(OfferProblems);
